import { Button, FlexWrapper, FontIcon, LinkWrapper, Text } from '@intigral/shared';
import { AlertDialog, Tier, Upgrade } from '@intigral/ui';
import Link from 'next/link';
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import h, { getCountry } from '../../helpers/common';
import user from '../../helpers/user';
import { showUpgradedModal } from '../../store/reducers/generalConfigurationSlice';
import { EVENT } from '../../contexts/MixpanelConstants';
import { getPaymentInAppMessageProperties } from '../../contexts/MixpanelHelper';
import { __ } from '../../translations';
import { redirect } from '@intigral/utils/lib/helpers/browser';
import mixpanel from 'mixpanel-browser';
import { getThreeLetterCountryCode } from '@intigral/utils';
import { Modal } from '../ui/Modal';
import { useConfigurationSections } from '../../hooks/useConfiguration';
import { isUserInGracePeriod, redirectToPaymentSelectionPage } from '@intigral/utils';
import { isIapOperator } from '../../utils/user';

const trackPaymentInAppMessageViewed = () => {
    mixpanel.track(EVENT.PAYMENT.IN_APP_MESSAGE_VIEWED, getPaymentInAppMessageProperties());
};

const trackPaymentInAppMessageAction = (userAction: 'Dismissed' | 'Update Payment') => {
    mixpanel.track(EVENT.PAYMENT.IN_APP_MESSAGE_ACTION, {
        ...getPaymentInAppMessageProperties(),
        'User Action': userAction,
    });
};

const IapPaymentFailedModal = ({
    isOpen,
    onClose,
}: {
    isOpen: boolean;
    onClose: () => void;
}) => (
    <Modal useFlexLayout={false} open={isOpen} onOpenChange={onClose} closePlacement="end" widthSet="medium">
        <FlexWrapper flexDirection="column" gap={{ default: '2x', md: '3x' }}>
            <FontIcon name="exclamation-mark" size="size60" md="size60" color="red" />
            <Text as="h3" variant="h3" margin="0" size={{ default: '22', md: '28' }}>
                {__('payment_failed_title')}
            </Text>
            <Text as="p" margin="0" color="gray" alignment="center">
                {__('payment_failed_description_iap')}
            </Text>
        </FlexWrapper>
    </Modal>
);

const useGracePeriodPaymentAnalytics = (isOpen: boolean, isGracePeriod: boolean) => {
    const hasTrackedViewRef = useRef(false);

    useEffect(() => {
        if (isOpen && isGracePeriod && !hasTrackedViewRef.current) {
            hasTrackedViewRef.current = true;
            trackPaymentInAppMessageViewed();
        }

        if (!isOpen) {
            hasTrackedViewRef.current = false;
        }
    }, [isOpen, isGracePeriod]);
};

const KidUpgradeModalContent = ({ handleCloseModal, isGracePeriod }) => {
    return (
        <>
            {
                <>
                    <Text as="h3" variant="h3" margin={{ default: '2x', md: '3x' }}>
                        {isGracePeriod ? __('kid_upgrade_modal_title_grace_period') : __('kid_upgrade_modal_title')}
                    </Text>

                    <Text as="p" variant="text3" margin={{ default: '2x', md: '3x' }}>
                        {isGracePeriod
                            ? __('kid_upgrade_modal_description_grace_period')
                            : __('kid_upgrade_modal_description')}
                    </Text>
                </>
            }

            <FlexWrapper
                flexDirection="column"
                gap={{ default: '2x', md: '3x' }}
                paddingTop={{ default: '2x', md: '4x' }}
            >
                <FlexWrapper direction={{ default: 'column', md: 'row' }} gap={{ default: '2x', md: '4x' }}>
                    <Button inverse onClick={handleCloseModal} fullWidth>
                        {__('cancel')}
                    </Button>
                </FlexWrapper>
            </FlexWrapper>
        </>
    );
};

const UpgradeConfirmModal = ({ isOpen, handleCloseModal }) => {
    const language = h.getLang();
    const isGracePeriod = isUserInGracePeriod();
    const { upgradedBannerModal: upgradedBannerModalConfig, avodConfigs, config, iap_operators } =
        useConfigurationSections();
    const dispatch = useDispatch();
    const upgradeModalFromNoAdsCta = useSelector(
        (state: { generalConfiguration?: { upgradeModalFromNoAdsCta?: boolean } }) =>
            state?.generalConfiguration?.upgradeModalFromNoAdsCta ?? false
    );
    const noAdsCtaSource = useSelector(
        (state: { generalConfiguration?: { noAdsCtaSource?: string | null } }) =>
            state?.generalConfiguration?.noAdsCtaSource ?? null
    );
    const isKidProfile = user.getProfileMode() === 'kid';
    const isIapUser = isIapOperator(user.getUserInfo()?.operator, iap_operators);
    const autoCloseTimerRef = useRef(null);

    useGracePeriodPaymentAnalytics(isOpen, isGracePeriod);

    const handleGracePeriodDismiss = () => {
        trackPaymentInAppMessageAction('Dismissed');
        dispatch(showUpgradedModal(false));
        handleCloseModal();
    };

    const handleGracePeriodUpdatePayment = () => {
        trackPaymentInAppMessageAction('Update Payment');
        redirectToPaymentSelectionPage(config?.externalLinks?.myAccount, language);
        dispatch(showUpgradedModal(false));
        handleCloseModal();
    };

    // No Ads CTA in fullscreen: modal must render inside fullscreen element (browser only shows content there)
    const [fullscreenEl, setFullscreenEl] = useState<HTMLElement | null>(null);
    useEffect(() => {
        const sync = () =>
            setFullscreenEl(
                typeof document !== 'undefined'
                    ? document.fullscreenElement ?? (document as any).webkitFullscreenElement ?? null
                    : null
            );
        sync();
        document.addEventListener('fullscreenchange', sync);
        document.addEventListener('webkitfullscreenchange', sync);
        return () => {
            document.removeEventListener('fullscreenchange', sync);
            document.removeEventListener('webkitfullscreenchange', sync);
        };
    }, []);

    const upgradedLink = language === 'en' ? upgradedBannerModalConfig?.link?.en : upgradedBannerModalConfig?.link?.ar;

    // Auto-close when opened from No Ad CTA after maxUpgradeStaySeconds
    useEffect(() => {
        if (!isOpen) {
            if (autoCloseTimerRef.current) {
                clearTimeout(autoCloseTimerRef.current);
                autoCloseTimerRef.current = null;
            }
            return;
        }
        if (!upgradeModalFromNoAdsCta) {
            return;
        }
        const maxStaySeconds = Number(avodConfigs?.noAdCta?.maxUpgradeStaySeconds) || 5;
        const delayMs = maxStaySeconds * 1000;
        autoCloseTimerRef.current = setTimeout(() => {
            autoCloseTimerRef.current = null;
            sessionStorage.setItem('noAdCta_wasLongStay', '1');
            dispatch(showUpgradedModal(false));
            handleCloseModal();
        }, delayMs);
        return () => {
            if (autoCloseTimerRef.current) {
                clearTimeout(autoCloseTimerRef.current);
                autoCloseTimerRef.current = null;
            }
        };
    }, [isOpen, upgradeModalFromNoAdsCta, avodConfigs?.noAdCta?.maxUpgradeStaySeconds, dispatch, handleCloseModal]);

    return (
        <>
            {isKidProfile ? (
                <Modal
                    useFlexLayout={false}
                    open={isOpen}
                    onOpenChange={() => {
                        if (isGracePeriod) {
                            trackPaymentInAppMessageAction('Dismissed');
                        }
                        dispatch(showUpgradedModal(false));
                        handleCloseModal();
                    }}
                    closePlacement={null}
                    widthSet="medium"
                >
                    <KidUpgradeModalContent
                        handleCloseModal={() => {
                            if (isGracePeriod) {
                                trackPaymentInAppMessageAction('Dismissed');
                            }
                            handleCloseModal();
                        }}
                        isGracePeriod={isGracePeriod}
                    />
                </Modal>
            ) : (
                <>
                    {isGracePeriod ? (
                        isIapUser ? (
                            <IapPaymentFailedModal isOpen={isOpen} onClose={handleGracePeriodDismiss} />
                        ) : (
                            <AlertDialog
                                open={isOpen}
                                title={__('payment_failed_title')}
                                acceptButton={__('update_payment_cta')}
                                cancelButton={__('cancel_cta')}
                                onConfirm={handleGracePeriodUpdatePayment}
                                onCancel={handleGracePeriodDismiss}
                            >
                                <Text as="p" variant="text3">
                                    {__('payment_failed_description')}
                                </Text>
                            </AlertDialog>
                        )
                    ) : (
                        <UpgradePopup
                            isOpen={isOpen}
                            handleCloseModal={() => {
                                dispatch(showUpgradedModal(false));
                                handleCloseModal();
                            }}
                            OnUpgradeClick={() => redirect(upgradedLink)}
                            isFromNoAdsCta={upgradeModalFromNoAdsCta}
                            noAdsCtaSource={noAdsCtaSource}
                            container={upgradeModalFromNoAdsCta ? fullscreenEl ?? undefined : undefined}
                        />
                    )}
                </>
            )}
        </>
    );
};

export default UpgradeConfirmModal;

export const UpgradePopup = ({
    isOpen,
    handleCloseModal,
    OnUpgradeClick,
    isFromNoAdsCta = false,
    noAdsCtaSource = null,
    container, // optional: render modal inside this element (e.g. fullscreen) for No Ads CTA
}: {
    isOpen: boolean;
    handleCloseModal: () => void;
    OnUpgradeClick: () => void;
    isFromNoAdsCta?: boolean;
    noAdsCtaSource?: string | null;
    container?: HTMLElement;
}) => {
    const language = h.getLang();
    const isGracePeriod = isUserInGracePeriod();
    // Using React Query instead of Redux for configuration
    const { upgradedBannerModal: upgradedBannerModalConfig, config, iap_operators } =
        useConfigurationSections() as any;
    const isIapUser = isIapOperator(user.getUserInfo()?.operator, iap_operators);
    const upgradedLink = language === 'en' ? upgradedBannerModalConfig?.link?.en : upgradedBannerModalConfig?.link?.ar;
    const learnMoreLink =
        language === 'en' ? upgradedBannerModalConfig?.learnMoreLink?.en : upgradedBannerModalConfig?.learnMoreLink?.ar;

    const translatedOptions = upgradedBannerModalConfig?.options?.keys.map((option) => ({
        text: __(option),
    }));

    const countryCode = getThreeLetterCountryCode(getCountry())?.toLowerCase();

    useGracePeriodPaymentAnalytics(isOpen, isGracePeriod);

    if (isGracePeriod) {
        const handleDismiss = () => {
            trackPaymentInAppMessageAction('Dismissed');
            handleCloseModal();
        };

        if (isIapUser) {
            return <IapPaymentFailedModal isOpen={isOpen} onClose={handleDismiss} />;
        }

        return (
            <AlertDialog
                open={isOpen}
                title={__('payment_failed_title')}
                acceptButton={__('update_payment_cta')}
                cancelButton={__('cancel_cta')}
                onConfirm={() => {
                    trackPaymentInAppMessageAction('Update Payment');
                    redirectToPaymentSelectionPage(config?.externalLinks?.myAccount, language);
                    handleCloseModal();
                }}
                onCancel={handleDismiss}
            >
                <Text as="p" variant="text3">
                    {__('payment_failed_description')}
                </Text>
            </AlertDialog>
        );
    }

    return (
        <Modal
            useFlexLayout={false}
            open={isOpen}
            {...(container && { container })}
            widthSet="medium"
            onOpenChange={() => {
                if (isFromNoAdsCta) {
                    mixpanel.track(EVENT.AVOD.NO_ADS_UPGRADE_TO_PREMIUM_EXIT_CLICK, {
                        'No Ads CTA Source': noAdsCtaSource || 'Ad',
                        'User ID': user.getUsername() ?? '',
                    });
                }
                handleCloseModal();
            }}
            closePlacement="end"
            alignSelf="bottom"
            padding="0"
            title={__('upgrade_plan')}
        >
            <Tier
                theme="black"
                picture={upgradedBannerModalConfig?.backgroundImage ?? ''}
                title={__('premium_tier')}
                price={{
                    price: __(`upgraded_banner_modal_plus_tier_price_${countryCode}`).includes(`upgraded_banner`)
                        ? __(`upgraded_banner_modal_plus_tier_price_default`)
                        : __(`upgraded_banner_modal_plus_tier_price_${countryCode}`),
                    currency: __(`upgraded_banner_modal_plus_tier_currency_${countryCode}`).includes(`upgraded_banner`)
                        ? __(`upgraded_banner_modal_plus_tier_currency_default`)
                        : __(`upgraded_banner_modal_plus_tier_currency_${countryCode}`),
                    description: __('upgraded_banner_modal_renew_price_des')
                        .replace(
                            '{renewFrequency}',
                            __(`upgraded_banner_modal_renew_frequency_${countryCode}`)?.includes(`upgraded_banner`)
                                ? __(`upgraded_banner_modal_renew_frequency_default`)
                                : __(`upgraded_banner_modal_renew_frequency_${countryCode}`)
                        )
                        .replace(
                            '{renewPrice}',
                            __(`upgraded_banner_modal_plus_tier_renew_price_${countryCode}`)?.includes(
                                `upgraded_banner`
                            )
                                ? __(`upgraded_banner_modal_plus_tier_renew_price_default`)
                                : __(`upgraded_banner_modal_plus_tier_renew_price_${countryCode}`)
                        )
                        .replace(
                            '{renewCurrency}',
                            __(`upgraded_banner_modal_plus_tier_currency_${countryCode}`)?.includes(`upgraded_banner`)
                                ? __(`upgraded_banner_modal_plus_tier_currency_default`)
                                : __(`upgraded_banner_modal_plus_tier_currency_${countryCode}`)
                        ),
                }}
                list={translatedOptions}
                buttonText={__(upgradedBannerModalConfig?.title?.key)}
                features={
                    <LinkWrapper
                        link={{
                            linkComponent: Link,
                            href: learnMoreLink,
                            text: __('discover_feature'),
                            transformLinkProps: (props) => ({ href: props.href }),
                        }}
                    />
                }
                ctaButton={
                    <Button
                        compact
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            if (isFromNoAdsCta) {
                                if (typeof sessionStorage !== 'undefined') {
                                    sessionStorage.setItem('noAdCta_proceededToUpgrade', '1');
                                }
                                const payload = {
                                    'No Ads CTA Source': noAdsCtaSource || 'Ad',
                                    'User ID': user.getUsername() ?? '',
                                };
                                mixpanel.track(EVENT.AVOD.UPGRADE_STARTED, payload);
                                setTimeout(() => redirect(upgradedLink), 300);
                            } else {
                                OnUpgradeClick();
                            }
                        }}
                        onKeyDown={(e) => e.key === 'Enter' && (e.currentTarget as HTMLElement).click()}
                    >
                        {__('upgrade_to_premium')}
                    </Button>
                }
            />
        </Modal>
    );
};
